import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { SectionWrapper } from '@components/Styles/CommonStyle';
import { theme } from '@components/Styles/theme';
import { Stack, Typography } from '@mui/material';
import { CustomTableContainer, CustomTable, CustomTableRow, CustomHeaderCell, CustomBodyCell } from '@components/Styles/TableStyle';
import { TableHead, TableBody } from '@mui/material';
import moment from 'moment';
import { formatAmount } from '@utils/formatter';

const ProjectCreateTableFinanceViewV2 = forwardRef(({getMonthlySale, getMonthlyPurchase, getMonthlyExternalStaff, getMonthlyManagementFee}, ref) => {
  // 당해년도
  const year = moment().year();

  const [sale, setSale] = useState([]);
  const [purchase, setPurchase] = useState([]);
  const [externalStaff, setExternalStaff] = useState([]);
  const [managementFee, setManagementFee] = useState([]);

  const saleAmountYear = sale.filter(r => r[0] === year).reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const purchaseAmountYear = purchase.filter(r => r[0] === year).reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const externalStaffAmountYear = externalStaff.filter(r => r[0] === year).reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const managementFeeAmountYear = managementFee.filter(r => r[0] === year).reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);

  // 수익 = 매출 - 매입 - 외부인건비 - 판관비 - 내부원가(15%)
  const revenueYear = saleAmountYear - purchaseAmountYear - externalStaffAmountYear - managementFeeAmountYear - Math.ceil(saleAmountYear * 0.15);
  // 영업이익 = 매출 - 매입 - 외부인건비
  const profitYear = saleAmountYear - purchaseAmountYear - externalStaffAmountYear;

  const saleAmount = sale.reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const purchaseAmount = purchase.reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const externalStaffAmount = externalStaff.reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);
  const managementFeeAmount = managementFee.reduce((acc, r) => acc + r.slice(2).reduce((s, v) => s + (+v || 0), 0), 0);

  // 수익 = 매출 - 매입 - 외부인건비 - 판관비 - 내부원가(15%)
  const revenue = saleAmount - purchaseAmount - externalStaffAmount - managementFeeAmount - Math.ceil(saleAmount * 0.15);
  // 영업이익 = 매출 - 매입 - 외부인건비
  const profit = saleAmount - purchaseAmount - externalStaffAmount; 

  useImperativeHandle(ref, () => ({
    callFinanceView: (financeType) => {
      // console.log("callFinanceView");
      // console.log("financeType", financeType);

      if (financeType === 'sale') {
        const sale = getMonthlySale();
        console.log("sale", sale);
        setSale(sale);
      } else if (financeType === 'purchase') {
        const purchase = getMonthlyPurchase();
        console.log("purchase", purchase);
        setPurchase(purchase);
      } else if (financeType === 'externalStaff') {
        const externalStaff = getMonthlyExternalStaff();
        console.log("externalStaff", externalStaff);
        setExternalStaff(externalStaff);
      } else if (financeType === 'managementFee') {
        const managementFee = getMonthlyManagementFee();
        console.log("managementFee", managementFee);
        setManagementFee(managementFee);
      }
    }
  }));

  return (
    <React.Fragment>
      <SectionWrapper>
        <Stack mb={2}>
          <Typography sx={theme.typography.h4}>실적현황</Typography>
        </Stack>
        <CustomTableContainer>
          <CustomTable merge>
            <TableHead>
              <CustomTableRow>
                <CustomHeaderCell />
                <CustomHeaderCell align="center">수익</CustomHeaderCell>
                <CustomHeaderCell align="center">영업이익</CustomHeaderCell>
                <CustomHeaderCell align="center">매출</CustomHeaderCell>
                <CustomHeaderCell align="center">매입</CustomHeaderCell>
                <CustomHeaderCell align="center">외부인건비</CustomHeaderCell>
                <CustomHeaderCell align="center">판관비</CustomHeaderCell>
                <CustomHeaderCell align="center">내부원가(15%)</CustomHeaderCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              <CustomTableRow>
                <CustomHeaderCell scope="row" align="center">
                  {year}
                </CustomHeaderCell>
                <CustomBodyCell align="right" style={{ color: revenueYear >= 0 ? 'blue' : 'red' }}>
                  {/* 수익 */}
                  {
                    formatAmount(Math.ceil(revenueYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right" style={{ color: profitYear >= 0 ? 'blue' : 'red' }}>
                  {/* 영업이익 */}
                  {
                    formatAmount(Math.ceil(profitYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 매출 */}
                  { 
                    formatAmount(Math.ceil(saleAmountYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 매입 */}
                  {
                    formatAmount(Math.ceil(purchaseAmountYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 외부인건비 */}
                  {
                    formatAmount(Math.ceil(externalStaffAmountYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 판관비 */}
                  {
                    formatAmount(Math.ceil(managementFeeAmountYear))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 내부원가(15%) */}
                  {
                    formatAmount(Math.ceil(saleAmountYear * 0.15))
                  }
                </CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell scope="row" align="center">
                  전체
                </CustomHeaderCell>
                <CustomBodyCell align="right" style={{ color: revenue >= 0 ? 'blue' : 'red' }}>
                  {/* 수익 */}
                  {
                    formatAmount(Math.ceil(revenue))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right" style={{ color: profit >= 0 ? 'blue' : 'red' }}>
                  {/* 영업이익 */}
                  {
                    formatAmount(Math.ceil(profit))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 매출 */}
                  {
                    formatAmount(Math.ceil(saleAmount))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 매입 */}
                  {
                    formatAmount(Math.ceil(purchaseAmount))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 외부인건비 */}
                  {
                    formatAmount(Math.ceil(externalStaffAmount))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 판관비 */}
                  {
                    formatAmount(Math.ceil(managementFeeAmount))
                  }
                </CustomBodyCell>
                <CustomBodyCell align="right">
                  {/* 내부원가(15%) */}
                  {
                    formatAmount(Math.ceil(saleAmount * 0.15))
                  }
                </CustomBodyCell>
              </CustomTableRow>
            </TableBody>
          </CustomTable>
        </CustomTableContainer>
      </SectionWrapper>
    </React.Fragment>
  )
});

export default ProjectCreateTableFinanceViewV2;